import { AppRouteName } from '@app/routes'
import { handleStoreError } from '@libs/errors/handleStoreError'
import type { MutationUpdateUserLatestReadReleaseNotesVersion } from '@server/graphql/mutations/user'
import { mutationUpdateUserLatestReadReleaseNotesVersion } from '@server/graphql/mutations/user'
import type { Maybe } from '@server/graphql/typeDefs/types'
import { action, computed, makeObservable, observable } from 'mobx'
import StoreFlags from '../helpers/StoreFlags'
import type { StoreRoot } from '../index'
import StoreBase from '../StoreBase'
import type { IStoreOptions } from '../types'
import { getMajorMinorNumbersFromVersion } from './helpers'

interface IReleaseNotesContent {
  version: string
  content: string
}

export default class StoreReleaseNotes extends StoreBase {
  public storeFlags = new StoreFlags(this.storeRoot)
  public storeFlagsGetContent = new StoreFlags(this.storeRoot)

  public translate = this.storeRoot.appTranslator.bindOptions({
    namespaces: ['Buttons', 'Login.EULA']
  })

  /* Observable */
  private $allReleaseNotes = observable.box<string[]>([])

  private $releaseNotesContents = observable.box<IReleaseNotesContent[]>([])

  private $releaseNotesContent = observable.box<string>('')

  constructor(storeRoot: StoreRoot, options: IStoreOptions = {}) {
    super(storeRoot, options)
    makeObservable(this)
  }

  /**
   * Fetch all Release Notes.
   */
  fetchAllReleaseNotes(): Promise<void> {
    if (this.storeRoot.stores.storeAbout.isSaaSDeployment) {
      return Promise.resolve()
    }

    this.storeFlags.loading()

    const url = this.storeRoot.appRouter.makeRouteInfosPathname({
      routeName: AppRouteName.MiddlewareReleaseNotes_GetAll,
      parameters: {}
    })

    return this.storeRoot.fetchClient
      .get(url)
      .then(response => {
        if (response.status !== 200) {
          throw new Error('Unable to get release notes')
        }

        return response.json() as Promise<string[]>
      })
      .then(data => {
        this.storeFlags.success()

        this.setAllReleaseNotes(data)
      })
      .catch(handleStoreError(this.storeRoot, this.storeFlags))
  }

  async fetchReleaseNotesContentSinceVersion(
    fromVersion: Maybe<string>
  ): Promise<void> {
    if (!this.allReleaseNotes) {
      this.setReleaseNotesContents([])
      return Promise.resolve()
    }

    this.storeFlagsGetContent.loading()

    // Try to find the fromVersion index in all release notes
    let fromIndex = this.allReleaseNotes.findIndex(
      releaseNotesVersion => releaseNotesVersion === fromVersion
    )

    // Try to get the version greater than the fromVersion
    if (fromIndex === -1 && fromVersion) {
      fromIndex = this.allReleaseNotes.findIndex(
        releaseNotesVersion => releaseNotesVersion > fromVersion
      )
    }

    // Create a new array containing all release notes including the fromVersion and the toVersion
    // While getting folders, it's sorted alphabetically so we need to reverse it to get the latest version first
    const releaseNotesToLoad = this.allReleaseNotes.slice(fromIndex).reverse()

    // Load all contents for all versions that are searched
    await Promise.all(
      releaseNotesToLoad.map(async releaseNotesVersion => {
        const url = this.storeRoot.appRouter.makeRouteInfosPathname({
          routeName: AppRouteName.MiddlewareReleaseNotes_GetContent,
          parameters: {
            version: releaseNotesVersion
          }
        })

        const response = await this.storeRoot.fetchClient.get(url)
        if (response.status !== 200) {
          throw new Error('Unable to get release notes')
        }
        return response.text()
      })
    )
      .then(contents => {
        this.setReleaseNotesContents(
          releaseNotesToLoad.map((version, index) => {
            return {
              version,
              content: contents[index]
            }
          })
        )

        this.storeFlagsGetContent.success()
      })
      .catch(handleStoreError(this.storeRoot, this.storeFlagsGetContent))
  }

  getReleaseNotesContent(version: string): void {
    this.storeFlagsGetContent.loading()

    const url = this.storeRoot.appRouter.makeRouteInfosPathname({
      routeName: AppRouteName.MiddlewareReleaseNotes_GetContent,
      parameters: {
        version
      }
    })

    this.storeRoot.fetchClient
      .get(url)
      .then(response => {
        if (response.status !== 200) {
          throw new Error('Unable to get release notes')
        }

        return response.text()
      })
      .then(data => {
        this.storeFlagsGetContent.success()

        this.setReleaseNoteContent(data)
      })
      .catch(handleStoreError(this.storeRoot, this.storeFlagsGetContent))
  }

  /**
   * Mark latest release notes as read by updating the user latestReadReleaseNotesVersion property.
   */
  markReleaseNotesVersionAsRead(): Promise<void> {
    this.storeFlags.loading()

    const { latestReleaseNote } = this.storeRoot.stores.storeReleaseNotes

    if (!latestReleaseNote) {
      return Promise.resolve()
    }

    return Promise.resolve()
      .then(() => {
        const whoAmI = this.storeRoot.stores.storeAuthentication.whoAmI

        if (!whoAmI) {
          throw new Error('User is not defined')
        }

        const args: MutationUpdateUserLatestReadReleaseNotesVersion['args'] = {
          userLatestReadReleaseNotesVersion: {
            userLatestReadReleaseNotesVersion: latestReleaseNote
          }
        }

        return this.storeRoot
          .getGQLRequestor()
          .makeQuery<MutationUpdateUserLatestReadReleaseNotesVersion>(
            mutationUpdateUserLatestReadReleaseNotesVersion,
            args
          )
      })
      .then(() => {
        this.storeRoot.stores.storeAuthentication.updateLatestReleaseNotesVersion(
          latestReleaseNote
        )

        this.storeFlags.success()
      })
      .catch(handleStoreError(this.storeRoot, this.storeFlags))
  }

  /* Actions */

  @action
  setAllReleaseNotes(folders: string[]) {
    this.$allReleaseNotes.set(folders)
  }

  @action
  setReleaseNotesContents(contents: IReleaseNotesContent[]) {
    this.$releaseNotesContents.set(contents)
  }

  @action
  setReleaseNoteContent(content: string) {
    this.$releaseNotesContent.set(content)
  }

  /* Computed */

  @computed
  get allReleaseNotes(): Maybe<string[]> {
    return this.$allReleaseNotes.get()
  }

  @computed
  get allReleaseNotesReversed(): Maybe<string[]> {
    return this.$allReleaseNotes.get()?.slice().reverse()
  }

  @computed
  get latestReleaseNote(): Maybe<string> {
    if (!this.allReleaseNotes || this.allReleaseNotes.length === 0) {
      return null
    }

    return this.allReleaseNotes[this.allReleaseNotes.length - 1]
  }

  /**
   * Return true if the current user has not approved the latest release notes or false if on SaaS.
   */
  @computed
  get needToReadLatestReleaseNotes(): boolean {
    if (this.storeRoot.stores.storeAbout.isSaaSDeployment) {
      return false
    }

    if (!this.latestReleaseNote) {
      return false
    }

    const { whoAmI } = this.storeRoot.stores.storeAuthentication

    if (!whoAmI) {
      return false
    }

    const { latestReadReleaseNotesVersion } = whoAmI

    if (!latestReadReleaseNotesVersion) {
      return true
    }

    const latestReadReleaseNotesVersionParsed = getMajorMinorNumbersFromVersion(
      latestReadReleaseNotesVersion
    )

    if (!latestReadReleaseNotesVersionParsed) {
      return false
    }

    const latestReleaseNoteParsed = getMajorMinorNumbersFromVersion(
      this.latestReleaseNote
    )

    if (!latestReleaseNoteParsed) {
      return false
    }

    // New major version release note available
    if (
      latestReleaseNoteParsed.major > latestReadReleaseNotesVersionParsed.major
    ) {
      return true
    }

    // Should not happen, we could reset the latest read in DB for this case
    if (
      latestReleaseNoteParsed.major < latestReadReleaseNotesVersionParsed.major
    ) {
      return false
    }

    // New minor version release note available
    if (
      latestReleaseNoteParsed.minor > latestReadReleaseNotesVersionParsed.minor
    ) {
      return true
    }

    // Should not happen, we could reset the latest read in DB for this case
    if (
      latestReleaseNoteParsed.minor < latestReadReleaseNotesVersionParsed.minor
    ) {
      return false
    }

    // Same version, no new release note available
    return false
  }

  @computed
  get releaseNotesContents(): IReleaseNotesContent[] {
    return this.$releaseNotesContents.get()
  }

  @computed
  get releaseNotesContent(): string {
    return this.$releaseNotesContent.get()
  }
}
